import React, { useContext, useEffect, useState } from 'react';
import Tippy, { TippyProps, useSingleton } from '@tippyjs/react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import { push } from 'connected-react-router';
import actions from 'src/actions';
import { UserTypes } from 'src/models/UserType';
import { UserRoles } from 'src/models/UserRole';
import Authorization from 'src/util/Authorization';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import selectors from 'src/selectors';
import UserDetails from 'src/models/UserDetails';
import Permissions from 'src/models/Permissions';

export const getDisplayRoleNames = (user: UserDetails): string[] => {
    if (user && user.roles && user.roles.length > 0) {
        return user.roles;
    }

    const legacyRoleName = user ? UserRoles[user.role] : undefined;
    return legacyRoleName ? [legacyRoleName] : [];
};

export const hasLeaseRecordsAccess = (user: UserDetails): boolean =>
    Authorization.HasPermission(Permissions.LeaseRecordsAccess, user);

const AppNavBar: React.FC = () => {
    type page =
        | 'home'
        | 'client'
        | 'land'
        | 'backoffice'
        | 'personnel'
        | 'leases'
        | 'accounts-payable'
        | 'accounts-receivable'
        | 'client-project'
        | 'project-billing'
        | 'reports'
        | 'admin'
        | 'profile'
        | 'gis'
        | 'invoicing';

    const appContext = useSelector((state: IRootState) => state.appContext);
    const dispatch = useDispatch();
    const [menu, setMenu] = useState({ task: false, profile: false, error: false });
    const [currentSection, setCurrentSection] = useState<page>(undefined);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const [source, target] = useSingleton(undefined);
    const user: UserDetails = useSelector(selectors.auth.user);

    // Code for the Report Error Button
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [emailed, setEmailed] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const displayRoleNames = getDisplayRoleNames(user);
    const hasLegacyAdminAccess =
        Authorization.IsInRole(UserRoles.Admin, appContext.currentUser) || Authorization.HasRole('Admin', user);

    // const userHasAccess =
    //     appContext.currentUser.role === UserRoles.HR ||
    //     appContext.currentUser.role === UserRoles.Admin ||
    //     appContext.currentUser.role === UserRoles.ProspectManager ||
    //     appContext.currentUser.role === UserRoles.ProjectCoordinator;
    const userHasBackOfficeAccess =
        appContext.currentUser.role === UserRoles.HR ||
        appContext.currentUser.role === UserRoles.HRAdmin ||
        appContext.currentUser.role === UserRoles.Admin ||
        appContext.currentUser.role === UserRoles.Accounting ||
        appContext.currentUser.role === UserRoles.AccountingAdmin ||
        // Tab visibility only - a Prospect Manager still sees everything Back Office exposes today
        // (Personnel/AP/AR/Billing/etc.) until the "block what PMs shouldn't see" access-scoping
        // task narrows that down; this just unblocks them from the gate itself, per 2026-09-14
        // direction. Note this covers every Prospect Manager, not only the project managers that
        // direction was originally about - the separate "Project Manager" role it introduced was
        // errant and has been removed in favour of the pre-existing Prospect Manager role/claim.
        appContext.currentUser.role === UserRoles.ProspectManager;
    const userHasGISAccess =
        appContext.currentUser.role === UserRoles.Admin ||
        appContext.currentUser.role === UserRoles.ProspectManager ||
        appContext.currentUser.role === UserRoles.ProjectCoordinator ||
        !!user.gisAccess; // todo? convert logic to same logic used in ProjectNavBar.tsx

    useEffect(() => {
        if (window.location.pathname.indexOf('/clients') >= 0) {
            setCurrentSection('land');
        } else if (window.location.pathname.indexOf('/personnel') >= 0) {
            setCurrentSection('personnel');
        } else if (window.location.pathname.indexOf('/accounts-payable') >= 0) {
            setCurrentSection('accounts-payable');
        } else if (window.location.pathname.indexOf('/client-project') >= 0) {
            setCurrentSection('client-project');
        } else if (window.location.pathname.indexOf('/project-billing') >= 0) {
            setCurrentSection('project-billing');
        } else if (window.location.pathname.indexOf('/reports') >= 0) {
            setCurrentSection('reports');
        } else if (window.location.pathname.indexOf('/admin') >= 0) {
            setCurrentSection('admin');
        } else if (window.location.pathname.indexOf('/profile') >= 0) {
            setCurrentSection('profile');
        } else if (window.location.pathname.indexOf('/gis') >= 0) {
            setCurrentSection('gis');
        } else if (window.location.pathname.indexOf('/leases') >= 0) {
            setCurrentSection('leases');
            // } else if (window.location.pathname.indexOf('/invoicing') >= 0) {
            //     setCurrentSection('invoicing');
        } else {
            setCurrentSection('home');
        }
    }, [window.location.pathname]);

    useEffect(() => {
        if (showDialog) {
            setTimeout(() => {
                if (!emailed || !copied) {
                    setShowDialog(false);
                }
            }, 4000);
        }
    }, [showDialog]);

    const navigate = (path: string) => {
        setMenu({ task: false, profile: false, error: false });
        dispatch(push(path));
    };

    const taskPanel = (
        <div className="menu-drop-down-panel">
            <div style={{ padding: '1.5rem' }}>In Dev...</div>
        </div>
    );

    const profilePanel = (
        <div className="menu-drop-down-panel">
            <div className="profile-role-type">{UserTypes[appContext.currentUser.type]}</div>
            <div className="profile-user-info">
                <div className="text-center profile-user-info-name">{appContext.currentUser.username}</div>
                <div className="text-center profile-user-info-email">{appContext.currentUser.email}</div>
                <div className="text-center profile-user-info-role">
                    {displayRoleNames.length === 1 ? 'Role' : 'Roles'}: {displayRoleNames.join(', ')}
                </div>
            </div>
            {(hasLegacyAdminAccess ||
                Authorization.HasAnyPermission([Permissions.RolesManage, Permissions.UserRolesManage], user)) && (
                <div className="panel-section admin-link" onClick={() => navigate(hasLegacyAdminAccess ? '/admin' : '/admin/roles')}>
                    <i className="fas fa-cog" /> Administration
                </div>
            )}
            <div className="panel-section profile-link" onClick={() => navigate('/profile')}>
                <i className="fas fa-id-card" /> Profile
            </div>
            <div className="panel-section logout-link" onClick={() => dispatch(actions.auth.logout())}>
                <i className="fas fa-sign-out" /> Logout
            </div>
        </div>
    );

    const reportErrorPanel = (
        <>
            <div style={{ margin: '0.5rem' }} className="menu-drop-down-panel">
                <div style={{ padding: '2px' }}>
                    <p style={{ margin: '0.5rem' }}>When reporting an error, please include: </p>
                    <ul>
                        <li>A basic summary of the issue</li>
                        <li>The url of the page you were on when you encountered the issue</li>
                        <li>Screenshot(s) of the error</li>
                        <li>Any relevant documents (if generated or trying to upload)</li>
                        <li>The client and project number (if applicable)</li>
                    </ul>
                </div>
                <div style={{ marginBottom: '0.5rem' }} className="text-center">
                    Please send all requested information to <a href="mailto:help@kudu.on.spiceworks.com">help@kudu.on.spiceworks.com</a>
                </div>
            </div>
        </>
    );

    const commonProps: Partial<TippyProps> = {
        placement: 'bottom-end',
        interactive: true,
        offset: [0, 4],
        delay: [0, 0],
        hideOnClick: true,
        trigger: 'click',
        onClickOutside: () => setMenu({ task: false, profile: false, error: false }),
    };

    const openSearch = () => {
        dialogContext.nav.searchDialog();
    };

    // const openSuperView = () => {
    //     dispatch(actions.layout.openSecondWindow());
    // };

    // const handleClick = () => {
    //    console.log('testing');
    // };

    const handleSendEmail = () => {
        setEmailed(true);
        setShowDialog(false);
    };

    const handleCopyEmail = async () => {
        const email = 'help@kudu.on.spiceworks.com';
        try {
            await navigator.clipboard.writeText(email);
            console.log('Email copied to clipboard');
            setCopied(true);
            setTimeout(() => {
                setShowDialog(false);
                setCopied(false);
            }, 1500);
        } catch (err) {
            console.log('Failed to copy: ', err);
        }
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            setShowDialog(false);
        }, 1000);
    };

    const hidePrimaryNavBarAndShowSecondary = () => {
        const primaryNavBarRef = document.getElementById('primaryNavBar');
        const secondaryNavBarRef = document.getElementById('secondaryNavBar');
        if (primaryNavBarRef) {
            primaryNavBarRef.style.display = 'none';
        }
        if (secondaryNavBarRef) {
            secondaryNavBarRef.style.display = 'block';
        }
        navigate('/personnel/home');
    };

    const hideSecondaryNavBarAndShowPrimary = () => {
        const primaryNavBarRef = document.getElementById('primaryNavBar');
        const secondaryNavBarRef = document.getElementById('secondaryNavBar');
        if (primaryNavBarRef) {
            primaryNavBarRef.style.display = 'block';
        }
        if (secondaryNavBarRef) {
            secondaryNavBarRef.style.display = 'none';
        }
        navigate('/');
    };

    return (
        <>
            <div className="navbar-wrapper">
                <div className="app-navbar">
                    <div className="centered">
                        <div className="left-aligned" onClick={() => dispatch(push('/'))}>
                            <span className="logo">{process.env.REACT_APP_TITLE}</span>
                        </div>
                        <div className="right-aligned">
                            <Tippy singleton={source} {...commonProps} />
                            <Tippy
                                singleton={target}
                                content={taskPanel}
                                visible={menu.task}
                                onClickOutside={() => setMenu({ ...menu, task: false })}
                            >
                                <span className="task-menu" onClick={() => setMenu({ profile: false, task: !menu.task, error: false })}>
                                    <i className="fas fa-tasks" /> Tasks
                                </span>
                            </Tippy>
                            <Tippy
                                singleton={target}
                                content={profilePanel}
                                visible={menu.profile}
                                onClickOutside={() => setMenu({ ...menu, profile: false })}
                            >
                                <span className="task-menu" onClick={() => setMenu({ task: false, profile: !menu.profile, error: false })}>
                                    <i className="fas fa-user-circle" /> {appContext.currentUser.username}
                                </span>
                            </Tippy>
                        </div>
                    </div>
                </div>
                <div id="primaryNavBar" className="app-second-navbar">
                    <div className="centered">
                        <div className={`link ${currentSection === 'home' && 'selected'}`} onClick={() => navigate('/')}>
                            Home
                        </div>
                        <div className={`link ${currentSection === 'land' && 'selected'}`} onClick={() => navigate('/clients')}>
                            Land
                        </div>
                        {userHasBackOfficeAccess && (
                            <div className={`link ${currentSection === 'backoffice' && 'selected'}`} onClick={hidePrimaryNavBarAndShowSecondary}>
                                Back Office
                            </div>
                        )}
                        {userHasGISAccess && (
                            <div className={`link ${currentSection === 'gis' && 'selected'}`} onClick={() => navigate('/gis/portal')}>
                                GIS
                            </div>
                        )}

                        {hasLeaseRecordsAccess(user) && (
                            <div className={`link ${currentSection === 'leases' && 'selected'}`} onClick={() => navigate('/leases')}>
                                Lease Records
                            </div>
                        )}
                        {/* {userHasAccess && <div className={`link ${currentSection === 'invoicing' && 'selected'}`} onClick={() => navigate('/invoicing')}>
                            Invoicing
                        </div>} */}
                        <div className="link" onClick={openSearch}>
                            <i className="fas fa-search" /> Search
                        </div>
                        {/* <div className="link" onClick={openSuperView} style={{ borderRight: '1px solid #457aac' }}>
                            <i className="fas fa-external-link" /> SuperView
                        </div> */}
                        <div className="report-error-link" onClick={() => setShowErrorDialog(!showErrorDialog)} style={{ marginLeft: '37vw' }}>
                            <Tippy
                                singleton={target}
                                content={reportErrorPanel}
                                visible={menu.error}
                                onClickOutside={() => setMenu({ ...menu, error: false })}
                            >
                                <span className="task-menu" onClick={() => setMenu({ task: false, profile: false, error: !menu.error })}>
                                    Report an Error
                                </span>
                            </Tippy>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'none' }} id="secondaryNavBar" className="app-second-navbar">
                    <div className="centered">
                        <div className={`link ${currentSection === 'home' && 'selected'}`} onClick={hideSecondaryNavBarAndShowPrimary}>
                            Home
                        </div>
                        {userHasBackOfficeAccess && (
                            <div className={`link ${currentSection === 'reports' && 'selected'}`} onClick={() => navigate('/reports')}>
                                Reports
                            </div>
                        )}
                        {userHasBackOfficeAccess && (
                            <div className={`link ${currentSection === 'personnel' && 'selected'}`} onClick={() => navigate('/personnel/home')}>
                                Personnel
                            </div>
                        )}
                        {userHasBackOfficeAccess && (
                            <div
                                className={`link ${currentSection === 'accounts-payable' && 'selected'}`}
                                onClick={() => navigate('/accounts-payable/vendors')}
                            >
                                AP
                            </div>
                        )}
                        {userHasBackOfficeAccess && (
                            <div
                                className={`link ${currentSection === 'accounts-receivable' && 'selected'}`}
                                onClick={() => navigate('/accounts-receivable/deposit-invoice-reconciliation')}
                            >
                                AR
                            </div>
                        )}
                        {userHasBackOfficeAccess && (
                            <div
                                className={`link ${currentSection === 'client-project' && 'selected'}`}
                                onClick={() => navigate('/client-project/clientmaintenance')}
                            >
                                C/P
                            </div>
                        )}
                        {userHasBackOfficeAccess && (
                            <div
                                className={`link ${currentSection === 'project-billing' && 'selected'}`}
                                onClick={() => navigate('/project-billing/officeexpenses')}
                            >
                                Billing
                            </div>
                        )}
                    </div>
                    {!!showDialog && (
                        <div
                            style={{
                                width: '300px',
                                position: 'absolute',
                                right: '8vw',
                                height: '50px',
                                display: 'flex',
                                gap: '2rem',
                                background: 'white',
                                alignItems: 'center',
                                border: '1px solid #457aac',
                                borderRadius: '5px',
                                justifyContent: 'space-around',
                            }}
                            onMouseLeave={handleMouseLeave}
                        >
                            <a onClick={handleSendEmail} href="mailto:help@kudu.on.spiceworks.com">
                                Send Email
                            </a>
                            <a onClick={handleCopyEmail}>{copied ? 'Copied!' : 'Copy Email'}</a>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AppNavBar;



// WEBPACK FOOTER //
// ./src/components/layout/AppNavBar.tsx